import { CalendarDate } from "@internationalized/date";
import {
  toISODateTime,
  type ISODateTime,
} from "@repo/app-types/entities/filters";
import { DateTime } from "luxon";
import type { CalendarDateRange } from "./dateRangePickerLayout.js";

/**
 * A range over upcoming days, as picked in `ForwardDateRangeDialog`.
 * `next_days` has no lower bound, so anything already overdue stays in it.
 * `custom` holds inclusive calendar days as ISO dates ("2026-10-05"), read in
 * the viewer's zone.
 */
export type ForwardDateRange =
  | { type: "any" }
  | { type: "next_days"; days: number }
  | { type: "custom"; start: string; end: string };

/**
 * The instants a {@link ForwardDateRange} covers, as ISO date-times:
 * `scheduledAfter` inclusive, `scheduledBefore` exclusive. A missing bound is
 * open.
 */
export interface ForwardDateRangeBounds {
  scheduledAfter?: ISODateTime;
  scheduledBefore?: ISODateTime;
}

const isoDateFormat = "yyyy-MM-dd";

/**
 * Resolves a range to instants, reading its days in `now`'s zone. Both bounds
 * fall on the start of a day, so a range always covers whole days: "next 7
 * days" is today and the 6 days after it, as "Last 7 days" counts today going
 * back, and a custom range ends with its `end` day. Each bound is the start of
 * its own day, not a day's start carried forward, since where a DST change
 * skips midnight a day starts at 01:00 and the next one at midnight again.
 * Throws on a malformed custom date rather than leaving that bound open.
 */
export function computeForwardDateRangeBounds(
  value: ForwardDateRange,
  now: DateTime<true>,
): ForwardDateRangeBounds {
  switch (value.type) {
    case "any":
      return {};
    case "next_days":
      return {
        scheduledBefore: toISODateTime(
          now.plus({ days: value.days }).startOf("day"),
        ),
      };
    case "custom": {
      const start = parseIsoDate(value.start, now);
      const end = parseIsoDate(value.end, now);
      if (!start || !end) {
        throw new Error(
          `Invalid custom date range: ${value.start} – ${value.end}`,
        );
      }
      return {
        scheduledAfter: toISODateTime(start),
        scheduledBefore: toISODateTime(end.plus({ days: 1 }).startOf("day")),
      };
    }
  }
}

/**
 * A filter sentence naming a {@link ForwardDateRange} — "is within next 7
 * days" — the forward counterpart of `formatDateRangeCondition`.
 */
export function formatForwardDateRangeCondition(
  value: ForwardDateRange,
): string {
  switch (value.type) {
    case "any":
      return "is at any time";
    case "next_days":
      return `is within next ${value.days} days`;
    case "custom":
      return `is within ${formatIsoDate(value.start)} – ${formatIsoDate(value.end)}`;
  }
}

/**
 * The inclusive days a range covers, for the calendar and the typed entry: the
 * same days {@link computeForwardDateRangeBounds} resolves it to, so editing a
 * preset into a custom range keeps its last day. `any` has no days of its own
 * and seeds today.
 */
export function convertForwardDateRangeToCalendarDays(
  value: ForwardDateRange,
  now: DateTime<true>,
): CalendarDateRange {
  const today = createCalendarDate(now);
  switch (value.type) {
    case "any":
      return { start: today, end: today };
    case "next_days":
      return { start: today, end: today.add({ days: value.days - 1 }) };
    case "custom": {
      const start = parseIsoDate(value.start, now);
      const end = parseIsoDate(value.end, now);
      return {
        start: start ? createCalendarDate(start) : today,
        end: end ? createCalendarDate(end) : today,
      };
    }
  }
}

/** Start of an ISO date's day in `now`'s zone; undefined if malformed. */
export function parseIsoDate(
  isoDate: string,
  now: DateTime<true>,
): DateTime<true> | undefined {
  const dateTime = DateTime.fromFormat(isoDate, isoDateFormat, {
    zone: now.zone,
  });
  return dateTime.isValid ? dateTime : undefined;
}

function formatIsoDate(isoDate: string): string {
  const dateTime = DateTime.fromFormat(isoDate, isoDateFormat);
  return dateTime.isValid
    ? dateTime.toLocaleString(DateTime.DATE_SHORT)
    : isoDate;
}

function createCalendarDate(dateTime: DateTime): CalendarDate {
  return new CalendarDate(dateTime.year, dateTime.month, dateTime.day);
}
